import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../../utils/fetchInterceptor';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

interface AgeGroup {
  _id: string;
  name: string;
  ageRange: string;
  description: string;
  icon: string;
  color: string;
  projectCount: number;
  difficulty: string;
  learningGoals: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AgeGroups: React.FC = () => {
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('All');
  const [editingGroup, setEditingGroup] = useState<AgeGroup | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const difficulties = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  // Fetch age groups from API
  const fetchAgeGroups = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/age-groups`
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch age groups");

      const data = await response.json();
      setAgeGroups(data.ageGroups || data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Error fetching age groups:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgeGroups();
  }, [fetchAgeGroups]);

  const filteredGroups = ageGroups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty = filterDifficulty === 'All' || group.difficulty === filterDifficulty;
    
    return matchesSearch && matchesDifficulty;
  });

  const toggleGroupStatus = async (id: string) => {
    try {
      setIsUpdating(true);
      const group = ageGroups.find(g => g._id === id);
      if (!group) return;

      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/age-groups/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isActive: !group.isActive }),
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to update age group status");

      await fetchAgeGroups(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update age group status");
      console.error("Error updating age group status:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteGroup = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this age group? This will affect all associated content.')) return;

    try {
      setIsDeleting(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/age-groups/${id}`,
        {
          method: 'DELETE',
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to delete age group");

      await fetchAgeGroups(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete age group");
      console.error("Error deleting age group:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const updateGroup = async (updatedGroup: AgeGroup) => {
    try {
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/age-groups/${updatedGroup._id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedGroup),
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to update age group");

      await fetchAgeGroups(); // Refresh the list
      setEditingGroup(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update age group");
      console.error("Error updating age group:", err);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Age Groups</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchAgeGroups}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Age Groups Management</h1>
          <p className="text-gray-600">Manage age-specific content (4-8, 9-13, 14-18)</p>
        </div>
        <button
          onClick={() => {
            // TODO: Implement add new age group functionality
            alert('Add new age group functionality coming soon!');
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <i className="fas fa-plus mr-2"></i>
          Add New Age Group
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search age groups..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {difficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>{difficulty}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterDifficulty('All');
              }}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Age Groups Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredGroups.map((group) => (
          <div key={group._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              {/* Group Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: group.color }}
                  >
                    <i className={`fas fa-${group.icon} text-xl`}></i>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
                    <p className="text-sm text-gray-500">Ages {group.ageRange}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleGroupStatus(group._id)}
                  disabled={isUpdating}
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    group.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
                >
                  {group.isActive ? 'Active' : 'Inactive'}
                </button>
              </div>

              {/* Group Description */}
              <p className="text-gray-600 text-sm mb-4">{group.description}</p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500">Projects</p>
                  <p className="text-lg font-semibold text-gray-900">{group.projectCount}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Difficulty</p>
                  <p className={`text-lg font-semibold ${
                    group.difficulty === 'Beginner' ? 'text-green-600' :
                    group.difficulty === 'Intermediate' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {group.difficulty}
                  </p>
                </div>
              </div>

              {/* Learning Goals */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Learning Goals:</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  {group.learningGoals.map((goal, index) => (
                    <li key={index} className="flex items-center">
                      <i className="fas fa-check text-green-500 mr-2"></i>
                      {goal}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button 
                  onClick={() => setEditingGroup(group)}
                  className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors"
                >
                  <i className="fas fa-edit mr-1"></i>
                  Edit
                </button>
                <button className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md text-sm hover:bg-green-700 transition-colors">
                  <i className="fas fa-eye mr-1"></i>
                  View
                </button>
                <button 
                  onClick={() => deleteGroup(group._id)}
                  disabled={isDeleting}
                  className={`bg-red-600 text-white px-3 py-2 rounded-md text-sm hover:bg-red-700 transition-colors ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-users text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Age Groups</p>
              <p className="text-2xl font-semibold text-gray-900">{ageGroups.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-check-circle text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Groups</p>
              <p className="text-2xl font-semibold text-gray-900">
                {ageGroups.filter(g => g.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-code text-2xl text-purple-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Projects</p>
              <p className="text-2xl font-semibold text-gray-900">
                {ageGroups.reduce((sum, group) => sum + group.projectCount, 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-graduation-cap text-2xl text-orange-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Learning Goals</p>
              <p className="text-2xl font-semibold text-gray-900">
                {ageGroups.reduce((sum, group) => sum + group.learningGoals.length, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Age Group Modal */}
      {editingGroup && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Age Group</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              updateGroup(editingGroup);
            }}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={editingGroup.name}
                      onChange={(e) => setEditingGroup({...editingGroup, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age Range</label>
                    <input
                      type="text"
                      value={editingGroup.ageRange}
                      onChange={(e) => setEditingGroup({...editingGroup, ageRange: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editingGroup.description}
                    onChange={(e) => setEditingGroup({...editingGroup, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                    <select
                      value={editingGroup.difficulty}
                      onChange={(e) => setEditingGroup({...editingGroup, difficulty: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                    <input
                      type="color"
                      value={editingGroup.color}
                      onChange={(e) => setEditingGroup({...editingGroup, color: e.target.value})}
                      className="w-full h-10 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingGroup(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgeGroups;
